import React from "react";
import ATMPageHeader from "src/components/UI/atoms/ATMPageHeader/ATMPageHeader";
import ATMPagination from "src/components/UI/atoms/ATMPagination/ATMPagination";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { usePermission } from "src/hooks/usePermission";

type Props = {
  rows: any[];
  paginationProps: {
    isTableLoading: boolean;
    totalItems: number;
    page: number;
    rowsPerPage: number;
    searchValue: string;
    setPage: (newPage: number) => void;
    setRowsPerPage: (newLimit: number) => void;
    setSearchValue: (newValue: string) => void;
    setIsOpenAddDialog: (newValue: boolean) => void;
  };
  onEdit: (id: string) => void;
};
const BannerCardSkeleton = () => {
  return (
    <div className="bg-white shadow rounded-md overflow-hidden border animate-pulse">
      <div className="w-full h-40 bg-gray-200" />
      <div className="h-5 bg-gray-200 mt-2 mx-3 rounded" />
      <div className="h-4 bg-gray-200 mt-2 mb-3 mx-10 rounded" />
    </div>
  );
};

const CMSBannerListing = ({
  rows,
  paginationProps: {
    isTableLoading,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    setPage,
    setRowsPerPage,
    setSearchValue,
    setIsOpenAddDialog,
  },
  onEdit,
}: Props) => {
  const moduleName = "CMS";

  const canEdit = usePermission(moduleName, "UPDATE");
  return (
    <div className="flex flex-col h-full ">
      {/* Page Header */}
      <div className="p-4">
        <ATMPageHeader
          moduleName="CMS"
          pageTitle="Banners"
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          buttonProps={{
            btnName: "Add new Banner",
            onClick: () => {
              setIsOpenAddDialog(true);
            },
          }}
          hideAddButton={rows.length > 0}
        />
      </div>

      {/* Cards Grid */}
      <div className="flex-1 overflow-auto p-4">
        {isTableLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <BannerCardSkeleton key={index} />
            ))}
          </div>
        ) : rows?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {rows.map((row: any, index: number) => {
              const options = [];

              if (canEdit) {
                options.push({
                  label: "Edit",
                  onClick: () => onEdit(row.id),
                });
              }

              return (
                <div
                  key={row?.id || index}
                  className="relative bg-white shadow rounded-md overflow-hidden border"
                >
                  {/* Action Menu (Top Right) */}
                  <div className="absolute top-2 right-2 z-10">
                    <ATMMenu options={options} />
                  </div>

                  {/* Banner Image */}
                  <img
                    src={row?.image}
                    alt="Banner"
                    className="w-full h-40 object-cover"
                  />

                  {/* Banner Title */}
                  <p className="p-3 text-center text-sm font-medium border-t">
                    {row?.name}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          // 🔹 Empty State
          <div className="flex justify-center items-center h-full text-gray-500">
            No Banners Found
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="p-4 py-2 flex justify-end">
        <ATMPagination
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          rowCount={totalItems}
          rows={rows}
        />
      </div>
    </div>
  );
};

export default CMSBannerListing;
